// Subscribes to the `sam:action` socket envelope and routes each action to a callback. This is a
// pure socket→callback bridge: the chat workspace (useSamWorkspace) supplies the handlers and owns
// all state. Re-delivered envelopes are deduped by (message_id + action payload) so an identical
// envelope isn't applied twice — but the *distinct* envelopes a single turn emits still all apply
// (every publish_action in a turn reuses the turn's message_id, so a panel-open and a later
// REFRESH share it; keying on message_id alone would silently drop the REFRESH).
import { useEffect, useRef } from 'react';
import { socketService } from '@/services/SocketService';
import {
  CLOSE_CARD,
  CONFIRMATION,
  CONFIRMATION_RESULT,
  DRAFT_COMMUNICATION,
  EXECUTE_NOTE_FOLLOWUPS,
  SET_NOTE_FOLLOWUP_INCLUDED,
  EXPAND_NOTE_FOLLOWUP,
  NAVIGATE_CALENDAR,
  NAVIGATE_CARDS,
  NO_VIEW_THIS_TURN,
  SHOW_FEEDBACK,
  NAVIGATE_ROUTE,
  OPEN_CARD,
  OPEN_NOTE_TAKER,
  STOP_NOTE_TAKER,
  REFRESH_TASK_COMMENTS,
  REFRESH_TASK_DETAIL,
  REFRESH_MEETING_DETAIL,
  REFRESH_PROPERTY_DETAIL,
  SWITCH_VIEW,
  OPEN_WORKFLOW_SETTINGS_PANEL,
  REFRESH_WORKFLOW_SETTINGS,
  OPEN_COMPANY_WORKFLOWS_PANEL,
  OPEN_PROPOSAL_PREPARED_VIEW,
  PROPOSAL_SURFACE_INTERACT,
  type ProposalSurfaceInteractPayload,
  REPLAY_RECENT_VIEW,
  OPEN_RECENT_VIEWS_MENU,
  OPEN_RECENT_VIEWS_MENU_EVENT,
  QUICK_QUOTE,
  EXPAND_ENGAGEMENT_QUICK_QUOTE,
  REFRESH_QUICK_QUOTE,
  EXPAND_ENGAGEMENT_TRANSITION_CAPTURE,
  EXPAND_ENGAGEMENT_ASSESSMENT_CAPTURE,
  OPEN_QUICK_QUOTE_MAP,
  type SamAction,
  type SamActionEnvelope,
  type SamCardEvent,
  type SamFacet,
  type SamModule,
  type SamPresentation,
} from '@/components/SAM/samActionTypes';
import type { CardDefinition } from '@/redux/internal-services/agentApi';
import { planEnvelope, routeCardEvent, type EnvelopeStep } from '@/components/SAM/cards/cardEventRouter';
import { EnvelopeAck, sendPresentationAck } from '@/components/SAM/cards/presentationAck';

// Workflow ('workflow' module) is a single-object surface (WorkflowPanel/WorkflowCard) — NOT a
// list rendered through the generic InlineCards. OPEN_WORKFLOW_PANEL and the reused
// OPEN_ENGAGEMENTS_PANEL (module: 'workflow', from list_pending_approvals) both end in `_PANEL`,
// so without this check they'd fall into the generic onApplyFilter/renderModuleCards (list) path
// below — which produces a `CardsMessage`, not the `WorkflowCardMessage` this surface needs. Same
// for REFRESH_WORKFLOW vs. the generic REFRESH_<module> handling. 'engagements' (a genuine list of
// Engagement records, from search_engagement) is NOT special-cased here and flows through the
// generic list pipeline unchanged.
//
// OPEN_WORKFLOW_SETTINGS_PANEL / REFRESH_WORKFLOW_SETTINGS also reuse module: 'workflow' (a
// SECOND, DISTINCT single-object surface — the tenant's whole workflow STRUCTURE, no engagement),
// and both also end in `_PANEL` / start with `REFRESH_` — so module+suffix alone can't tell them
// apart from OPEN_WORKFLOW_PANEL/REFRESH_WORKFLOW. Every branch below checks the literal
// action_type string first, so the settings pair is intercepted before the generic engagement pair.
const isWorkflowModuleAction = (a: SamAction) => a.module === 'workflow';

interface UseSamActionsOptions {
  /** Called when SAM emits a guardrailed-tool confirmation card. */
  onConfirmation?: (action: SamAction, presentation?: SamPresentation | null) => void;
  /** Called when a guardrailed tool's execution finishes — reports the real outcome (success/
   *  failed/cancelled) for the confirmation card matching `call_id`. */
  onConfirmationResult?: (action: SamAction) => void;
  /**
   * SAM activated a module with a filter (OPEN_*_PANEL / APPLY_*_FILTER). The socket carries only
   * {module, filter, facets, title, caption, record_id} — the UI fetches the rows itself via REST.
   * title/caption (LLM-authored) head the workspace view + sit behind its "⋯" menu (the mandatory
   * display). `recordId` is the record a detail-opening read is bound to (e.g. get_engagement); it
   * is what makes the client's "⋯" entry key match the one publish_action recorded server-side.
   * `view` carries the menu-ONLY labels (view_title/view_caption) a write can attach to name its
   * "⋯" entry after the record it just created — kept apart from title/caption so it never spawns
   * an inline chat card, and so the live entry reads the same as the persisted one.
   */
  onApplyFilter?: (module: SamModule, filter: Record<string, unknown>, facets?: SamFacet[], title?: string, caption?: string, messageId?: string, recordId?: string | null, view?: { title?: string; caption?: string }) => void;
  /** A REFRESH_<module> action — re-fetch the module with its last filter. messageId is the
   *  write turn's id, so the refreshed card can re-point its reply strip to THIS turn's response. */
  onRefresh?: (module: SamModule, messageId?: string) => void;
  /**
   * A list/panel read carried an LLM-authored title/caption — optionally ALSO append an inline
   * "action card" to the chat thread (gated by config). Fires once per module per envelope.
   */
  onActionCard?: (module: SamModule, filter: Record<string, unknown>, title?: string, caption?: string, recordId?: string | null) => void;
  /**
   * Navigate to a card. recordId: open by id; index: open by 0-based position; recordId=null =
   * close detail. module may be undefined on a generic close → close whichever card is open.
   * filter: present when this OPEN_*_DETAIL rides in the same publish_action call as an
   * APPLY_*_FILTER for the same record (e.g. create_task's combined "list + detail in one
   * envelope") — same `fields.filter` on every item in that call. Passed through so the
   * resulting "⋯" recent-views entry keys the same as the sibling APPLY_*_FILTER's, instead of
   * computing a filter-less key that never merges with it (see contextRefFor/recentViewKey).
   */
  onOpenCard?: (module: SamModule | undefined, recordId: string | null, index?: number, filter?: Record<string, unknown>) => void;
  /** SWITCH_VIEW — flip the surface between the chat thread and the workspace/cards. */
  onSwitchView?: (target: 'chat' | 'workspace') => void;
  /** NAVIGATE_CARDS — hands-free swipe: step 'next'/'prev', or jump to a 1-based position. */
  /** Returns a reason string when there was nothing to step through (reported in the card trace). */
  onNavigateCards?: (opts: { direction?: 'next' | 'prev'; position?: number }) => string | void;
  /** OPEN_NOTE_TAKER — open the note-capture card in the workspace and start recording. */
  onOpenNoteTaker?: (opts?: { contextType?: string; entityId?: string }) => void;
  /** STOP_NOTE_TAKER — stop the active note recording (SAM stop_note_taker). */
  onStopNoteTaker?: () => void;
  /** EXECUTE_NOTE_FOLLOWUPS — trigger "Create all selected" on the open note review card. */
  onExecuteNoteFollowups?: () => void;
  /** SET_NOTE_FOLLOWUP_INCLUDED — check/uncheck one, several, or all follow-up suggestions. */
  onSetNoteFollowupIncluded?: (opts: { suggestionId?: string; suggestionIds?: string[]; included: boolean }) => void;
  /** EXPAND_NOTE_FOLLOWUP — expand a follow-up's review/edit panel, optionally prefilling fields. */
  onExpandNoteFollowup?: (opts: { suggestionId: string; fields?: Record<string, unknown> }) => void;
  /** DRAFT_COMMUNICATION — SAM drafted (or live-edited) an email/SMS/in-app message for the user to
   *  review + send. messageId is the turn id, so the draft card can show the matching reply. */
  onDraftCommunication?: (action: SamAction, messageId?: string) => void;
  /** QUICK_QUOTE — SAM produced a satellite-map + drawing-tools roof estimate card for an address.
   *  messageId is the turn id, so the card can show the matching reply. */
  onQuickQuote?: (action: SamAction, messageId?: string) => void;
  /** EXPAND_ENGAGEMENT_QUICK_QUOTE — SAM opened the Quick Quote section inside an engagement's
   *  WorkflowCard (open_engagement_quick_quote tool). Expand the embedded section, not a new card. */
  onExpandEngagementQuickQuote?: (engagementId: string) => void;
  /** REFRESH_QUICK_QUOTE — SAM saved a Quick Quote; invalidate RTK cache so the card refetches. */
  onRefreshQuickQuote?: (engagementId: string) => void;
  /** EXPAND_ENGAGEMENT_TRANSITION_CAPTURE — a blocked transition's business_capture form (e.g.
   *  schedule_assessment) should open on the WorkflowCard, mirroring a click on that transition's
   *  button (list_available_transitions tool). */
  onExpandEngagementTransitionCapture?: (engagementId: string, transitionCode: string) => void;
  /** EXPAND_ENGAGEMENT_ASSESSMENT_CAPTURE — SAM was asked to add a photo/voice note/report/note
   *  it can't accept itself (request_assessment_media_capture tool); expand + flash the
   *  Assessment meeting & capture card so the user can find the real button. */
  onExpandEngagementAssessmentCapture?: (engagementId: string) => void;
  /** OPEN_QUICK_QUOTE_MAP — SAM asked to open the satellite map inside the Quick Quote section. */
  onOpenQuickQuoteMap?: (engagementId: string) => void;
  /** NAVIGATE_ROUTE — push a frontend route (e.g. '/tasks/new', '/tasks/:id'). */
  onNavigateRoute?: (path: string) => void;
  /** REFRESH_TASK_COMMENTS — invalidate RTK Query cache for a task's comments. */
  onRefreshTaskComments?: (taskId: string) => void;
  /** REFRESH_TASK_DETAIL — invalidate RTK Query cache for a specific task (after update_task). */
  onRefreshTaskDetail?: (taskId: string) => void;
  /** REFRESH_MEETING_DETAIL — invalidate RTK Query cache for a specific meeting (after
   *  reschedule_meeting / cancel_meeting / add_meeting_note). */
  onRefreshMeetingDetail?: (meetingId: string) => void;
  /** REFRESH_PROPERTY_DETAIL — invalidate RTK Query cache for a specific property (after
   *  execute_workflow_transition changes its active engagement's stage/status). */
  onRefreshPropertyDetail?: (propertyId: string) => void;
  /** NAVIGATE_CALENDAR — move or reframe the on-screen calendar workspace. */
  onNavigateCalendar?: (opts: { direction?: string; unit?: string; view?: string; date?: string }) => void;
  /** OPEN_WORKFLOW_PANEL, or OPEN_ENGAGEMENTS_PANEL with module 'workflow' (list_pending_approvals'
   *  approvals-only view) — (re-)open the single Workflow surface, optionally bound to an
   *  engagement (record_id). messageId links it to the turn's assistant reply. */
  /** Returns a reason string when it deliberately rendered nothing (reported in the card trace). */
  onOpenWorkflowPanel?: (engagementId: string | undefined, title: string | undefined, caption: string | undefined, messageId: string | undefined) => string | void;
  /** REFRESH_WORKFLOW — invalidate the open Workflow surface's state/transitions/approvals cache
   *  (execute_workflow_transition / decide_workflow_approval hit the workflow service directly,
   *  bypassing the frontend's own RTK mutations). */
  onRefreshWorkflow?: (engagementId: string | undefined) => void;
  /** OPEN_WORKFLOW_SETTINGS_PANEL (get_workflow_customization) — (re-)open the Workflow SETTINGS
   *  surface (the tenant's whole workflow STRUCTURE editor; no engagement scope). messageId links
   *  it to the turn's assistant reply. */
  onOpenWorkflowSettingsPanel?: (title: string | undefined, caption: string | undefined, messageId: string | undefined) => void;
  /** REFRESH_WORKFLOW_SETTINGS — invalidate the open Workflow Settings surface's cache (the
   *  customization mutation tools hit the customization service directly, bypassing the frontend's
   *  own RTK mutations). */
  onRefreshWorkflowSettings?: () => void;
  /** OPEN_COMPANY_WORKFLOWS_PANEL (list_company_workflows) — open the company processes overview
   *  list card. The user clicks "Customize" on the card to open the full settings editor. */
  onOpenCompanyWorkflowsPanel?: (title: string | undefined, caption: string | undefined, messageId: string | undefined) => void;
  /** OPEN_PROPOSAL_PREPARED_VIEW (proposal_prepared_open tool) — trigger one of the three read-only
   *  panels on the Proposal Prepared card: "preview_proposal", "view_estimate", "open_assessment".
   *  engagementId scopes the action to the right WorkflowCard. */
  onOpenProposalPreparedView?: (engagementId: string, view: 'preview_proposal' | 'view_estimate' | 'open_assessment') => void;
  /** PROPOSAL_SURFACE_INTERACT (proposal_surface_interact tool) — an interaction INSIDE an
   *  already-open Proposal Prepared surface: option switch, section expand/collapse, item open,
   *  close, or scroll. Payload is passed through as-is; the consumer resolves it per surface. */
  onProposalSurfaceInteract?: (payload: ProposalSurfaceInteractPayload) => void;
  /** REPLAY_RECENT_VIEW (list_recent_views / open_recent_view) — reopen a "⋯" menu entry the
   *  agent picked, by reusing the SAME dispatch `replayView` already applies to a user's own menu
   *  click: module + filter + an optional bound record, plus the row's own title/caption. */
  onReplayRecentView?: (module: SamModule, filter: Record<string, unknown>, recordId: string | null, title?: string, caption?: string, contextRefType?: string) => void;
  /** cards_version 2 — the envelope's card lifecycle events, delivered BEFORE its actions are
   *  routed, so the card a following panel-open / filter action renders can be stamped with its instance id. */
  onCardEvents?: (events: SamCardEvent[], messageId?: string) => void;
  /** sam_cards.client_event_routing — route known cards on generic card events (cardEventRouter). */
  routeCardEvents?: boolean;
  /** Card catalogue (client config data.cards) — needed by the event router. */
  cardCatalogue?: CardDefinition[];
  /** The conversation this chat page shows. An envelope for a DIFFERENT conversation (another tab /
   *  device of the same user receives every agent:action) is not applied here. */
  conversationId?: string;
  /** SHOW_FEEDBACK — a non-blocking toast decided by the card rulebook. */
  onFeedback?: (fb: { level: 'INFO' | 'SUCCESS' | 'WARNING'; messageCode: number; durationMs: number; params?: Record<string, string | number> }) => void;
}

/** Whether a legacy action OPENS a record's detail card (and so adds a "⋯" menu entry).
 *  REFRESH_*_DETAIL only re-syncs a record in place — it must never open it (it once added a
 *  bogus "Contacts" entry after create_contact on the engagement screen). */
export function isRecordOpenAction(actionType: string): boolean {
  const t = actionType || '';
  // Allowlist, not a substring match: REFRESH_*_DETAIL / CLOSE_*_DETAIL must never open a card.
  if (t === OPEN_CARD || t.includes('OPEN_CARD')) return true;
  return t.startsWith('OPEN_') && t.endsWith('_DETAIL');
}

export function useSamActions(options: UseSamActionsOptions = {}) {
  const optsRef = useRef(options);
  optsRef.current = options;
  const seenRef = useRef<Set<string>>(new Set());
  // message_ids whose turn opened/refreshed the engagement Workflow surface. Defense-in-depth
  // against SAM incidentally calling an unrelated read tool (search_meetings, search_tasks, ...) in the
  // SAME turn as a workflow action — without this, that read's OPEN_*_PANEL steals the workspace
  // surface from the engagement Workflow card the user was just looking at (see
  // useChatWorkspace's `latestSurface`, which is simply "whichever surface message is newest").
  // A turn that ONLY touches another module (a genuine standalone request) is never added here, so
  // it still switches the surface normally.
  const workflowTurnIdsRef = useRef<Set<string>>(new Set());

  useEffect(() => {
    // Card navigation (open/close) — handled here so a generic close works even with no module.
    // Returns true when the action was a card open/close that this handled.
    const routeNavigation = (a: SamAction): boolean => {
      const t = a.action_type || '';
      const module = a.module as SamModule | undefined;
      if (t === CLOSE_CARD || t === 'BACK' || t === 'BACK_TO_LIST' || t.includes('CLOSE_CARD')) {
        optsRef.current.onOpenCard?.(module, null);
        return true;
      }
      if (!module) return false;
      if (isRecordOpenAction(t)) {
        optsRef.current.onOpenCard?.(module, a.record_id ?? null, a.index, a.filter);
        return true;
      }
      return false;
    };

    const onAction = (raw: unknown) => {
      const env = raw as SamActionEnvelope;

      // Another conversation's turn (this user's other tab / device) — not this screen's business,
      // confirmations included. No ack: only the tab that owns the conversation reports.
      const current = optsRef.current.conversationId;
      if (env.session_id && current && env.session_id !== current) {
        // api-service stamps session_id = the conversation id (actions.py ctx["session_id"]), so a
        // mismatch really is another conversation. Logged so a wrongly-dropped envelope is visible.
        console.debug('[SAM_ACTION] dropped envelope for another conversation', { envelope: env.session_id, current });
        return;
      }

      if (env.action && env.action.action_type === CONFIRMATION) {
        optsRef.current.onConfirmation?.(env.action, env.presentation);
        return;
      }
      if (env.action && env.action.action_type === CONFIRMATION_RESULT) {
        optsRef.current.onConfirmationResult?.(env.action);
        return;
      }

      const actions = env.actions ?? (env.action ? [env.action] : []);
      if (!actions.length) return;

      // Dedup on the envelope content, not just the turn id: message_id is per-turn (reused by
      // every publish_action in the turn), so a panel-open + a later REFRESH share it. Keying on
      // message_id + the actions payload drops true re-deliveries while letting each distinct
      // envelope in a turn through (so a write's REFRESH actually re-fetches the cards).
      const mid = env.message_id;
      if (mid) {
        const sig = `${mid}|${JSON.stringify(actions)}`;
        if (seenRef.current.has(sig)) return;
        seenRef.current.add(sig);
        if (seenRef.current.size > 200) seenRef.current = new Set([sig]);
      }

      if (env.card_events?.length) optsRef.current.onCardEvents?.(env.card_events, mid);

      // An envelope carrying `presentation` was decided by the backend card rulebook (cards/policy.py,
      // rule workflow-lock included) — the client executes it as-is. Only legacy envelopes (policy
      // engine off) still get the client-side workflowTurnIdsRef suppression below.
      const backendDecides = !!env.presentation;
      // Card trace: what this client actually did with each action (presentationAck.ts).
      const ack = env.run_id ? new EnvelopeAck(actions.map(a => a.action_type || '')) : null;
      const ignore = (a: SamAction, reason: string) => ack?.note(actions.indexOf(a), 'ignored', reason);

      // A read tool emits OPEN_*_PANEL + APPLY_*_FILTER together — trigger the fetch once per module.
      const applied = new Set<string>();
      // cards_version 3: with sam_cards.client_event_routing on, known cards are routed on their
      // generic card event (cardEventRouter); everything else — and every envelope when the flag is
      // off — goes through the legacy per-action handler below, in original order.
      const handleLegacyAction = (a: SamAction) => {
        const t = a.action_type || '';
        // View-control actions carry no module/data — handle and skip the module/filter logic.
        if (t === SWITCH_VIEW) {
          if (a.target === 'chat' || a.target === 'workspace') optsRef.current.onSwitchView?.(a.target);
          else ignore(a, 'invalid_target');
          return;
        }
        if (t === SHOW_FEEDBACK) {
          const code = Number(a.message_code);
          if (!Number.isInteger(code) || code <= 0) { ignore(a, 'missing_message_code'); return; }
          const level = a.level;
          if (!optsRef.current.onFeedback) { ignore(a, 'no_feedback_handler'); return; }
          optsRef.current.onFeedback({
            level: level === 'SUCCESS' || level === 'WARNING' ? level : 'INFO',
            messageCode: code,
            durationMs: Number(a.duration_ms) || 4000,
            params: a.params && typeof a.params === 'object' ? a.params as Record<string, string | number> : undefined,
          });
          return;
        }
        if (t === NO_VIEW_THIS_TURN) return; // informational: the card on screen is from an earlier turn
        if (t === NAVIGATE_CARDS) {
          const nothing = optsRef.current.onNavigateCards?.({ direction: a.direction as 'next' | 'prev' | undefined, position: a.position });
          if (nothing) ignore(a, nothing);
          return;
        }
        if (t === OPEN_NOTE_TAKER) {
          optsRef.current.onOpenNoteTaker?.({ contextType: a.context_type, entityId: a.entity_id });
          return;
        }
        if (t === STOP_NOTE_TAKER) {
          optsRef.current.onStopNoteTaker?.();
          return;
        }
        if (t === EXECUTE_NOTE_FOLLOWUPS) {
          optsRef.current.onExecuteNoteFollowups?.();
          return;
        }
        if (t === SET_NOTE_FOLLOWUP_INCLUDED) {
          optsRef.current.onSetNoteFollowupIncluded?.({
            suggestionId: a.suggestion_id, suggestionIds: a.suggestion_ids, included: a.included ?? true,
          });
          return;
        }
        if (t === EXPAND_NOTE_FOLLOWUP) {
          if (a.suggestion_id) optsRef.current.onExpandNoteFollowup?.({ suggestionId: a.suggestion_id, fields: a.fields });
          return;
        }
        if (t === DRAFT_COMMUNICATION) {
          optsRef.current.onDraftCommunication?.(a, mid);
          return;
        }
        if (t === QUICK_QUOTE) {
          optsRef.current.onQuickQuote?.(a, mid);
          return;
        }
        if (t === EXPAND_ENGAGEMENT_QUICK_QUOTE) {
          if (a.engagement_id) optsRef.current.onExpandEngagementQuickQuote?.(a.engagement_id as string);
          else ignore(a, 'missing_engagement_id');
          return;
        }
        if (t === REFRESH_QUICK_QUOTE) {
          if (a.engagement_id) optsRef.current.onRefreshQuickQuote?.(a.engagement_id as string);
          else ignore(a, 'missing_engagement_id');
          return;
        }
        if (t === EXPAND_ENGAGEMENT_TRANSITION_CAPTURE) {
          if (a.engagement_id && a.transition_code) {
            optsRef.current.onExpandEngagementTransitionCapture?.(a.engagement_id as string, a.transition_code as string);
          }
          return;
        }
        if (t === EXPAND_ENGAGEMENT_ASSESSMENT_CAPTURE) {
          if (a.engagement_id) optsRef.current.onExpandEngagementAssessmentCapture?.(a.engagement_id as string);
          else ignore(a, 'missing_engagement_id');
          return;
        }
        if (t === OPEN_QUICK_QUOTE_MAP) {
          if (a.engagement_id) optsRef.current.onOpenQuickQuoteMap?.(a.engagement_id as string);
          else ignore(a, 'missing_engagement_id');
          return;
        }
        // Reopen a "⋯" recent-views entry — module-agnostic, checked before the generic
        // PANEL/FILTER handling below since this carries its own module but isn't a list/panel
        // read in the usual sense (no facets, no row fetch triggered from here).
        if (t === OPEN_RECENT_VIEWS_MENU) {
          window.dispatchEvent(new Event(OPEN_RECENT_VIEWS_MENU_EVENT));
          return;
        }
        if (t === REPLAY_RECENT_VIEW && a.module) {
          optsRef.current.onReplayRecentView?.(a.module, a.filter ?? {}, a.record_id ?? null, a.title, a.caption, a.context_ref_type);
          return;
        }
        // Company Processes list card — checked before OPEN_WORKFLOW_SETTINGS_PANEL (both are
        // workflow-module actions but this one is the overview step, not the editor).
        if (t === OPEN_COMPANY_WORKFLOWS_PANEL) {
          optsRef.current.onOpenCompanyWorkflowsPanel?.(a.title, a.caption, mid);
          return;
        }
        if (t === OPEN_PROPOSAL_PREPARED_VIEW && a.engagement_id && a.view) {
          optsRef.current.onOpenProposalPreparedView?.(
            a.engagement_id as string,
            a.view as 'preview_proposal' | 'view_estimate' | 'open_assessment',
          );
          return;
        }
        if (t === PROPOSAL_SURFACE_INTERACT && a.engagement_id && a.surface && a.action) {
          optsRef.current.onProposalSurfaceInteract?.(a as unknown as ProposalSurfaceInteractPayload);
          return;
        }
        // Workflow SETTINGS single-object surface — checked by literal action_type BEFORE the
        // generic workflow-engagement pair below (both reuse module: 'workflow' and the
        // `_PANEL`/`REFRESH_` suffix pattern, so module+suffix alone can't tell them apart).
        if (t === OPEN_WORKFLOW_SETTINGS_PANEL) {
          optsRef.current.onOpenWorkflowSettingsPanel?.(a.title, a.caption, mid);
          return;
        }
        if (t === REFRESH_WORKFLOW_SETTINGS) {
          optsRef.current.onRefreshWorkflowSettings?.();
          return;
        }
        // Workflow (engagement) single-object surface — intercept before the generic PANEL/REFRESH_
        // handling below (see isWorkflowModuleAction's comment for why).
        if (isWorkflowModuleAction(a) && t.endsWith('_PANEL')) {
          if (mid) {
            workflowTurnIdsRef.current.add(mid);
            if (workflowTurnIdsRef.current.size > 200) workflowTurnIdsRef.current = new Set([mid]);
          }
          const skipped = optsRef.current.onOpenWorkflowPanel?.(a.record_id ?? undefined, a.title, a.caption, mid);
          if (skipped) ignore(a, skipped);
          return;
        }
        if (isWorkflowModuleAction(a) && t.startsWith('REFRESH_')) {
          if (mid) {
            workflowTurnIdsRef.current.add(mid);
            if (workflowTurnIdsRef.current.size > 200) workflowTurnIdsRef.current = new Set([mid]);
          }
          optsRef.current.onRefreshWorkflow?.(a.record_id ?? undefined);
          return;
        }
        if (t === NAVIGATE_ROUTE) {
          if (a.path) optsRef.current.onNavigateRoute?.(a.path);
          return;
        }
        if (t === REFRESH_TASK_COMMENTS) {
          if (a.record_id) optsRef.current.onRefreshTaskComments?.(a.record_id);
          return;
        }
        if (t === REFRESH_TASK_DETAIL) {
          if (a.record_id) optsRef.current.onRefreshTaskDetail?.(a.record_id);
          return;
        }
        if (t === REFRESH_MEETING_DETAIL) {
          if (a.record_id) optsRef.current.onRefreshMeetingDetail?.(a.record_id);
          return;
        }
        if (t === REFRESH_PROPERTY_DETAIL) {
          if (a.record_id) optsRef.current.onRefreshPropertyDetail?.(a.record_id);
          return;
        }
        if (t === NAVIGATE_CALENDAR) {
          optsRef.current.onNavigateCalendar?.({
            direction: a.direction,
            unit: a.unit,
            view: a.view,
            date: a.date,
          });
          return;
        }
        // This turn already opened/refreshed the Workflow surface — an unrelated module's read
        // (e.g. search_meetings called right after execute_workflow_transition) must not steal the
        // workspace surface away from the engagement the user was just looking at.
        const suppressSurfaceSwitch = !backendDecides && !!mid && workflowTurnIdsRef.current.has(mid) && a.module !== 'workflow';
        if (suppressSurfaceSwitch && a.module && (t.includes('FILTER') || t.endsWith('_PANEL') || t.startsWith('REFRESH_'))) {
          // Not "ignored" if the navigation router below still handled it.
          if (!routeNavigation(a)) ignore(a, 'legacy_rule:workflowTurnIdsRef');
        } else {
          routeNavigation(a);
        }
        if (!suppressSurfaceSwitch && (t.includes('FILTER') || t.endsWith('_PANEL')) && a.module && !applied.has(a.module)) {
          applied.add(a.module);
          // Opening a panel list view should collapse ANY currently open detail card, regardless
          // of module — e.g. task detail open → "show calendar" must clear it. Passing undefined
          // triggers the "clear all open cards" branch in useChatWorkspace onOpenCard.
          optsRef.current.onOpenCard?.(undefined as unknown as SamModule, null);
          optsRef.current.onApplyFilter?.(a.module, a.filter ?? {}, a.facets, a.title, a.caption, mid, a.record_id ?? null, { title: a.view_title, caption: a.view_caption });
          // Optional inline chat card (config-gated) — only for reads carrying a title/caption.
          if (a.title || a.caption) {
            optsRef.current.onActionCard?.(a.module, a.filter ?? {}, a.title, a.caption, a.record_id ?? null);
          }
        }
        if (!suppressSurfaceSwitch && t.startsWith('REFRESH_') && a.module) optsRef.current.onRefresh?.(a.module, mid);
      };
      const noteEvent = (ev: SamCardEvent, result: 'ignored' | 'error', reason: string) => {
        actions.forEach((a, i) => {
          if (a.card_definition_id === ev.card_definition_id && ev.action_types.includes(a.action_type)) ack?.note(i, result, reason);
        });
      };
      const routeCtx = {
        messageId: mid,
        applied,
        suppressSurfaceSwitch: (m: string) => !backendDecides && !!mid && workflowTurnIdsRef.current.has(mid) && m !== 'workflow',
        onIgnored: (ev: SamCardEvent, reason: string) => noteEvent(ev, 'ignored', reason),
      };
      const steps: EnvelopeStep[] = optsRef.current.routeCardEvents
        ? planEnvelope(actions, env.card_events, optsRef.current.cardCatalogue)
        : actions.map(action => ({ kind: 'action' as const, action }));
      for (const step of steps) {
        try {
          if (step.kind === 'event') routeCardEvent(step.event, step.card, optsRef.current, routeCtx);
          else handleLegacyAction(step.action);
        } catch (err) {
          // One broken action must not stop the rest of the envelope — and the trace must say so.
          console.error('[SAM_ACTION] handler failed', err);
          if (step.kind === 'action') ack?.note(actions.indexOf(step.action), 'error', String(err).slice(0, 160));
          else noteEvent(step.event, 'error', String(err).slice(0, 160));
        }
      }
      if (ack && env.run_id) sendPresentationAck(ack.build(env.run_id, mid, env.presentation?.decision));
    };

    // Listen on both the shared `agent:action` channel (text / PTT / realtime CRM tools
    // via publish_action) and the realtime-prefixed `agent_realtime:action` channel
    // (actions emitted by the realtime session's _drain_actions loop).
    socketService.on('agent:action', onAction);
    socketService.on('agent_realtime:action', onAction);
    return () => {
      socketService.off('agent:action', onAction);
      socketService.off('agent_realtime:action', onAction);
    };
  }, []);
}
